// File: webelieve-frontend/src/features/admin/components/settings/intake-forms/FieldEditor.tsx
// EF-6.1: Field property editor — right sidebar in the form builder

import React from 'react';
import type { FormField, FormFieldOption, FormFieldType } from './intakeFormApi';
import { FIELD_ICONS } from './FieldPalette';

export type EditableField = FormField & { _localId: string };

interface FieldEditorProps {
  field: EditableField;
  allFields: EditableField[];
  onChange: (patch: Partial<EditableField>) => void;
  onRemove: () => void;
}

const INPUT_CLASS = 'w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent';

const TYPES_WITH_VALIDATION: FormFieldType[] = ['text', 'email', 'phone', 'textarea', 'number'];
const TYPES_WITH_OPTIONS: FormFieldType[] = ['select', 'multiselect'];

const FieldEditor: React.FC<FieldEditorProps> = ({ field, allFields, onChange, onRemove }) => {
  const hasValidation = TYPES_WITH_VALIDATION.includes(field.type);
  const hasOptions = TYPES_WITH_OPTIONS.includes(field.type);
  const otherFields = allFields.filter(f => f._localId !== field._localId);

  const updateOption = (index: number, patch: Partial<FormFieldOption>) => {
    const opts = [...(field.options || [])];
    opts[index] = { ...opts[index], ...patch };
    onChange({ options: opts });
  };

  const addOption = () => {
    const opts = [...(field.options || [])];
    opts.push({ value: `option_${opts.length + 1}`, label: `Option ${opts.length + 1}` });
    onChange({ options: opts });
  };

  const removeOption = (index: number) => {
    const opts = [...(field.options || [])];
    opts.splice(index, 1);
    onChange({ options: opts });
  };

  return (
    <div className="w-72 flex-shrink-0 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
      <div className="p-3 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm">{FIELD_ICONS[field.type]}</span>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Edit Field
            </h3>
          </div>
          <button
            onClick={onRemove}
            className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400"
          >
            Remove
          </button>
        </div>

        <div className="text-[10px] text-gray-400 dark:text-gray-500 uppercase font-medium">
          Type: {field.type}
        </div>

        {/* Label */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Label</label>
          <input
            type="text"
            value={field.label}
            onChange={e => onChange({ label: e.target.value })}
            className={INPUT_CLASS}
          />
        </div>

        {/* Placeholder */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Placeholder</label>
          <input
            type="text"
            value={field.placeholder || ''}
            onChange={e => onChange({ placeholder: e.target.value || undefined })}
            placeholder="Enter placeholder text..."
            className={INPUT_CLASS}
          />
        </div>

        {/* Required */}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={field.required}
            onChange={e => onChange({ required: e.target.checked })}
            className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Required</span>
        </label>

        {/* Validation */}
        {hasValidation && (
          <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-[10px] font-medium text-gray-400 uppercase">Validation</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">
                  {field.type === 'number' ? 'Min Value' : 'Min Length'}
                </label>
                <input
                  type="number"
                  value={field.validation?.min ?? ''}
                  onChange={e => onChange({
                    validation: { ...field.validation, min: e.target.value ? Number(e.target.value) : undefined },
                  })}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">
                  {field.type === 'number' ? 'Max Value' : 'Max Length'}
                </label>
                <input
                  type="number"
                  value={field.validation?.max ?? ''}
                  onChange={e => onChange({
                    validation: { ...field.validation, max: e.target.value ? Number(e.target.value) : undefined },
                  })}
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 mb-0.5">Regex Pattern</label>
              <input
                type="text"
                value={field.validation?.pattern || ''}
                onChange={e => onChange({
                  validation: { ...field.validation, pattern: e.target.value || undefined },
                })}
                placeholder="e.g. ^[A-Z].*"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 mb-0.5">Error Message</label>
              <input
                type="text"
                value={field.validation?.message || ''}
                onChange={e => onChange({
                  validation: { ...field.validation, message: e.target.value || undefined },
                })}
                placeholder="Custom error message"
                className={INPUT_CLASS}
              />
            </div>
          </div>
        )}

        {/* Options (select/multiselect) */}
        {hasOptions && (
          <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-[10px] font-medium text-gray-400 uppercase">Options</p>
            {(field.options || []).map((opt, i) => (
              <div key={i} className="flex items-center gap-1">
                <input
                  type="text"
                  value={opt.label}
                  onChange={e => updateOption(i, { label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="Option label"
                  className={`flex-1 ${INPUT_CLASS}`}
                />
                <button
                  onClick={() => removeOption(i)}
                  className="text-red-400 hover:text-red-600 text-xs px-1"
                  title="Remove option"
                >
                  &times;
                </button>
              </div>
            ))}
            <button
              onClick={addOption}
              className="text-xs text-lex-accent dark:text-lex-accent hover:underline"
            >
              + Add option
            </button>
          </div>
        )}

        {/* Conditional Logic */}
        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <p className="text-[10px] font-medium text-gray-400 uppercase">Conditional Logic</p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!field.conditional_logic}
              onChange={e => {
                if (e.target.checked && otherFields.length > 0) {
                  onChange({
                    conditional_logic: {
                      show_if_field: otherFields[0].id,
                      show_if_value: '',
                    },
                  });
                } else {
                  onChange({ conditional_logic: undefined });
                }
              }}
              className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
            />
            <span className="text-xs text-gray-700 dark:text-gray-300">Show conditionally</span>
          </label>
          {field.conditional_logic && (
            <div className="space-y-2 ml-5">
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">When field</label>
                <select
                  value={field.conditional_logic.show_if_field}
                  onChange={e => onChange({
                    conditional_logic: { ...field.conditional_logic!, show_if_field: e.target.value },
                  })}
                  className={INPUT_CLASS}
                >
                  {otherFields.map(f => (
                    <option key={f.id} value={f.id}>{f.label || f.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Equals</label>
                <input
                  type="text"
                  value={field.conditional_logic.show_if_value || ''}
                  onChange={e => onChange({
                    conditional_logic: { ...field.conditional_logic!, show_if_value: e.target.value },
                  })}
                  placeholder="Value to match"
                  className={INPUT_CLASS}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldEditor;
